import React, { useState, useRef } from "react";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import Select from "react-select";

import { Modal, Grid, Backdrop, Fade, Checkbox } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import CloseIcon from '@mui/icons-material/Close';
import SoftTypography from "../../../components/SoftTypography";
import SoftBox from "../../../components/SoftBox";
import SoftInput from "../../../components/SoftInput";
import SoftButton from "../../../components/SoftButton";
import GreenToggle from "../../../components/GreenToggle";

import PageHeader from "../../../components/PageHeader";
import ProfilePicture from "./ProfilePicture";
import IntegratedAccounts from "./IntegratedAccounts";

import AccountSection from "./layout/AccountSection";
import AccountButton from "./components/AccountButton";
import AccountPassword from "./components/AccountPassword";
import PhoneCodePanel from "./components/PhoneCodePanel";
import usePhoneVerification from "./hooks/usePhoneVerification";

import { CANCEL_SUBSCRIPTION } from "../../../api/subscription";


// Icons
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Birthday field — month/day only (no year). Numbers match the User schema
// (birthday.month is 1-based). Feeds the birthday automation trigger.
const BIRTHDAY_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const BIRTHDAY_DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const BIRTHDAY_MONTH_OPTIONS = BIRTHDAY_MONTHS.map((m, i) => ({ value: i + 1, label: m }));
const buildDayOptions = (month) => {
  const max = month ? BIRTHDAY_DAYS_IN_MONTH[month - 1] : 31;
  return Array.from({ length: max }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
};

const BRAND_BLUE = "var(--gmb-primary, #1B598E)";
// Selected → brand tint, hovered → faint gray, otherwise transparent.
const birthdayOptionBg = (state) => {
  if (state.isSelected) return "rgba(27,89,142,0.10)";
  if (state.isFocused) return "rgba(15,23,42,0.05)";
  return "transparent";
};
const birthdaySelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "48px",
    borderRadius: "8px",
    background: "#F9FAFB",
    border: state.isFocused ? `1.5px solid ${BRAND_BLUE}` : "1px solid #CED4DA",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(27,89,142,0.08)" : "0px 2px 3px 0px #0000000D",
    fontFamily: "DM Sans",
    fontSize: "14px",
    cursor: "pointer",
    "&:hover": { borderColor: state.isFocused ? BRAND_BLUE : "#98A2B3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 14px" }),
  placeholder: (base) => ({ ...base, color: "#98A2B3", fontWeight: 400 }),
  singleValue: (base) => ({ ...base, color: "#495057", fontWeight: 500 }),
  dropdownIndicator: (base) => ({ ...base, color: "#98A2B3" }),
  indicatorSeparator: () => ({ display: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(26px) saturate(185%)",
    WebkitBackdropFilter: "blur(26px) saturate(185%)",
    border: "1px solid rgba(255,255,255,0.6)",
    padding: "6px",
    boxShadow: "0 16px 40px -12px rgba(16,24,40,0.22), inset 0 1px 0 rgba(255,255,255,0.7)",
    overflow: "hidden",
  }),
  menuList: (base) => ({ ...base, padding: 0, maxHeight: "220px" }),
  option: (base, state) => ({
    ...base,
    fontFamily: "DM Sans",
    fontSize: "14px",
    fontWeight: state.isSelected ? 600 : 500,
    borderRadius: "6px",
    padding: "9px 10px",
    cursor: "pointer",
    background: birthdayOptionBg(state),
    color: state.isSelected ? BRAND_BLUE : "#475467",
  }),
};

const AccountSettingsV2 = ({
  userDetails,
  isLoading,
  isAdmin,
  fileSelectedCallback,
  handleFormSubmit,
  handleLogout,
  handleSendResetPassword,
  UseUserId
}) => {

  const [cancelForm, setShowcancelForm] = useState(true);
  const [supportContent, setSupport] = useState(false);

  const handleBtnSupport = () => {
    setSupport(true);
    setShowcancelForm(false);
  };

  const [selected, setSelected] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(false);

  const handleChange = (event) => {
    const { value, checked } = event.target;
    setCheckboxValue(event.target.value);
    if (checked && value === 'Other') {
      setSelected(event.target.name);
      setShowInput(true);
    } else {
      setSelected(event.target.name);
      setShowInput(false);
    }
  };

  const [openModal, setOpenModal] = useState(false);
  const [confirmation, setConfirmation] = useState(true);

  const [cancellationDone, setconfirmCancellation] = useState(false);

  const handleBtnConfirmation = () => {
    setConfirmation(false);
  };
  
  const handleCancelForm = () => {
    setOpenModal(true);
  };
  const formRef = useRef(null);

  const confirmCancellation = async () => {
    setconfirmCancellation(true);
    if (formRef.current) {
      const body = {
        userId: UseUserId,
        status: 'cancelled',
        reasonForCancellation: checkboxValue,
      };
      await CANCEL_SUBSCRIPTION(body);
    }
  };

  const handleCloseModal = () => {
    setconfirmCancellation(false);
    setSupport(false);
    setOpenModal(false);
    setConfirmation(true);
    setShowcancelForm(true);
  };

  // glass modal paper (light)
  const style = {
    position: 'absolute',
    top: '35%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)',
    borderRadius: '24px',
    padding: '1.75rem',
    maxWidth: { mini: "90%", xs: "90%", sm: "600px" },
    width: "100%",
    overflow: "visible",
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    boxShadow: '0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.6)'
  };

  const {
    register,
    setValue,
    getValues,
    control,
    formState: { errors }
  } = useFormContext();

  // SMS opt-in is rendered as a custom GreenToggle (so its color stays
  // green when on — the MUI Switch theme paints the thumb brand-blue).
  // Read the live form value with useWatch so the toggle is controlled.
  const smsOptIn = useWatch({
    control,
    name: "smsOptIn",
    defaultValue: userDetails?.smsOptIn !== false,
  });

  const typedPhone = useWatch({
    control,
    name: "phoneNumber",
    defaultValue: userDetails?.phoneNumber || "",
  });
  const phoneVerify = usePhoneVerification({
    userId: UseUserId,
    userDetails,
    typedPhone,
    getTypedPhone: () => getValues("phoneNumber"),
  });

  // Watch the birthday month so the Day dropdown narrows to that month's length.
  const monthWatch = useWatch({
    control,
    name: "birthday.month",
    defaultValue: userDetails?.birthday?.month || null,
  });

  const inputFieldStyles = {
    border: "1px solid #CED4DA",
    borderRadius: "8px",
    boxShadow: "0px 2px 3px 0px #0000000D",
    padding: "12px 20px",
    backgroundColor: "#F9FAFB !important",
    "& input": {
      fontFamily: "DM Sans",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "14px",
      color: "#495057"
    },
  };

  const commonTitleStyles = {
    fontFamily: "DM Sans",
    fontWeight: 500
  }

  const [confirmText, setConfirmText] = useState('');

  // Add this handler for the confirm input
  const handleConfirmTextChange = (event) => {
    setConfirmText(event.target.value);
  };

  return (
    <>
      <PageHeader iconKey="accountsettings" headerText="Account Settings" />

      <SoftBox
        sx={{
          margin: { mini: "1rem", sm: "1.5rem", md: "2rem" },
          "& h4": {
            ...commonTitleStyles,
            fontSize: "16px",
            lineHeight: "24px",
            color: "#667085",
            marginBottom: "8px",
          },
          "& h5": { // subtitles
            ...commonTitleStyles,
            fontSize: "12px",
            lineHeight: "18px",
            color: "#344054"
          },
        }}
      >
        <form onSubmit={handleFormSubmit}>
          <SoftBox sx={{ maxWidth: "760px", margin: "0 auto" }}>
            <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
              <div>
                <p className="text-sm text-gray-400 mt-0 mb-0">Manage your profile and preferences</p>
              </div>
              <AccountButton
                type="submit"
                disabled={isLoading}
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 600,
                  fontSize: "13px",
                  lineHeight: "16px",
                  border: "none",
                  borderRadius: "8px",
                  color: "#FFF !important",
                  backgroundColor: "#111827 !important",
                  "&:hover": { backgroundColor: "#1F2937 !important" },
                  flexShrink: 0,
                }}
              >
                Save changes
              </AccountButton>
            </div>

            {/** Profile Picture */}
            <ProfilePicture
              fileSelectedCallback={fileSelectedCallback}
              disabled={isLoading}
              userDetails={userDetails}
            />

            {/** Name Section */}
            <AccountSection
              icon={PersonOutlineIcon}
              title="Personal info"
              subtitle="Edit/View the name on your account"
            >
              <Grid container spacing={{ mini: 2, sm: 3 }}>
                <Grid item mini={12} sm={6}>
                  <SoftTypography variant="h4">
                    First Name
                  </SoftTypography>
                  <SoftInput
                    name="firstName"
                    placeholder="First Name"
                    type="text"
                    autoComplete="off"
                    sx={inputFieldStyles}
                    disabled={isLoading}
                    {...register("firstName")}
                    error={!!errors?.firstName}
                    helperText={errors?.firstName?.message}
                  />
                </Grid>
                <Grid item mini={12} sm={6}>
                  <SoftTypography variant="h4">
                    Last Name
                  </SoftTypography>
                  <SoftInput
                    name="lastName"
                    placeholder="Last Name"
                    type="text"
                    autoComplete="off"
                    sx={inputFieldStyles}
                    disabled={isLoading}
                    {...register("lastName")}
                    error={!!errors?.lastName}
                    helperText={errors?.lastName?.message}
                  />
                </Grid>
              </Grid>
            </AccountSection>

            {/** Password Section */}
            <AccountSection
              icon={LockOutlinedIcon}
              title="Password"
              subtitle="Change your password by simply entering the old and New password"
            >
              <Grid container spacing={{ mini: 2, sm: 3 }}>
                <AccountPassword
                  label="Current Password"
                  name="oldPassword"
                  disabled={isLoading}
                  handleRegister={register}
                  errors={errors}
                />

                <AccountPassword
                  label="New Password"
                  name="newPassword"
                  disabled={isLoading}
                  handleRegister={register}
                  errors={errors}
                />

                <AccountPassword
                  label="Confirm Password"
                  name="confirmNewPassword"
                  disabled={isLoading}
                  handleRegister={register}
                  errors={errors}
                />
              </Grid>

              <p className="text-sm text-gray-700 mt-4 mb-0">
                Forgot your password?{" "}
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={handleSendResetPassword}
                  className="text-indigo-600 font-semibold underline bg-transparent border-none p-0 cursor-pointer disabled:opacity-50"
                >
                  Reset it
                </button>
              </p>
            </AccountSection>

            {/** Phone number & SMS performance alerts — Twilio/A2P opt-in UI */}
            <AccountSection
              icon={NotificationsOutlinedIcon}
              title="Contact and SMS alerts"
              subtitle="Optional. Not required to use the app."
            >
              <Grid container spacing={{ mini: 2, sm: 3 }} alignItems="flex-end">
                <Grid item mini={12} sm={8}>
                  <SoftTypography variant="h4">
                    Mobile number
                  </SoftTypography>
                  <SoftInput
                    name="phoneNumber"
                    placeholder="+1 555 123 4567"
                    type="tel"
                    autoComplete="tel"
                    sx={inputFieldStyles}
                    disabled={isLoading}
                    {...register("phoneNumber")}
                    error={!!errors?.phoneNumber}
                    helperText={errors?.phoneNumber?.message}
                  />
                </Grid>
                <Grid item mini={12} sm={4}>
                  <AccountButton
                    variant="outline"
                    type="button"
                    disabled={isLoading || phoneVerify.busy || phoneVerify.verified}
                    onClick={phoneVerify.sendCode}
                    sx={{
                      fontFamily: "DM Sans",
                      fontWeight: 500,
                      fontSize: "13px",
                      border: "1px solid #CED4DA",
                      borderRadius: "6px",
                      color: "#344054",
                      backgroundColor: "#FFFFFF !important",
                      width: "100%",
                      height: "44px",
                    }}
                  >
                    {phoneVerify.verified ? "Verified" : "Verify number"}
                  </AccountButton>
                </Grid>

                {phoneVerify.step === "code" && !phoneVerify.verified && (
                  <Grid item mini={12}>
                    <PhoneCodePanel
                      busy={phoneVerify.busy}
                      cooldown={phoneVerify.cooldown}
                      onConfirm={phoneVerify.confirmCode}
                      onResend={phoneVerify.sendCode}
                      onCancel={phoneVerify.cancel}
                    />
                  </Grid>
                )}

                <Grid item mini={12}>
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "16px",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border: "1px solid #EAECF0",
                      background: "#F9FAFB",
                    }}
                  >
                    <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                      <SoftTypography
                        sx={{
                          fontFamily: "DM Sans",
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#191D23",
                        }}
                      >
                        I agree to receive recurring transactional text messages from Game My Biz
                      </SoftTypography>
                      <SoftTypography
                        component="p"
                        sx={{
                          fontFamily: "DM Sans",
                          fontSize: "12px",
                          color: "#667085",
                          marginTop: "6px",
                          lineHeight: 1.5,
                        }}
                      >
                        By enabling this toggle, you consent to receive SMS from Game My Biz
                        about performance alerts, leaderboard updates, scorecard notifications,
                        achievements, milestones, account activity, and other operational
                        account notifications. Message frequency varies. Message and data rates
                        may apply. Reply STOP to opt out or HELP for help, or turn this toggle
                        off any time. Consent is optional and not required to use the platform.
                        In-app notifications stay on either way. See our{" "}
                        <a
                          href="https://gamemybiz.com/terms-condition"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#2563EB", textDecoration: "underline" }}
                        >
                          SMS Terms
                        </a>
                        {" "}and{" "}
                        <a
                          href="https://gamemybiz.com/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#2563EB", textDecoration: "underline" }}
                        >
                          Privacy Policy
                        </a>
                        .
                      </SoftTypography>
                    </SoftBox>
                    <GreenToggle
                      checked={smsOptIn}
                      onChange={() => setValue("smsOptIn", !smsOptIn)}
                      disabled={isLoading}
                      ariaLabel="I agree to receive recurring transactional text messages from Game My Biz"
                    />
                  </SoftBox>
                </Grid>
              </Grid>
            </AccountSection>

            {/** Birthday — self-serve month + day (no year). Feeds birthday automations. */}
            <AccountSection
              icon={CakeOutlinedIcon}
              title="Birthday"
              subtitle="Add your birthday so your team can celebrate you. We only use the month and day — no year."
            >
              <Grid container spacing={{ mini: 2, sm: 3 }}>
                <Grid item mini={12} sm={6}>
                  <SoftTypography variant="h4">
                    Month
                  </SoftTypography>
                  <Controller
                    name="birthday.month"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={BIRTHDAY_MONTH_OPTIONS}
                        value={BIRTHDAY_MONTH_OPTIONS.find((o) => o.value === field.value) || null}
                        onChange={(opt) => field.onChange(opt?.value ?? null)}
                        placeholder="Select month"
                        isClearable
                        isSearchable={false}
                        isDisabled={isLoading}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPlacement="auto"
                        styles={birthdaySelectStyles}
                      />
                    )}
                  />
                </Grid>
                <Grid item mini={12} sm={6}>
                  <SoftTypography variant="h4">
                    Day
                  </SoftTypography>
                  <Controller
                    name="birthday.day"
                    control={control}
                    render={({ field }) => {
                      const dayOpts = buildDayOptions(monthWatch);
                      return (
                        <Select
                          options={dayOpts}
                          value={dayOpts.find((o) => o.value === field.value) || null}
                          onChange={(opt) => field.onChange(opt?.value ?? null)}
                          placeholder="Select day"
                          isClearable
                          isSearchable={false}
                          isDisabled={isLoading}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                          menuPlacement="auto"
                          styles={birthdaySelectStyles}
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </AccountSection>

            {/** App Release Version Number */}
            <AccountSection
              title="App Version"
              subtitle="GameMyBiz App Release Number"
            >
              {(process.env.REACT_APP_VERSION)
                ? <SoftTypography variant="h4">
                    {process.env.REACT_APP_VERSION}
                  </SoftTypography>
                : <SoftTypography variant="caption" sx={{ fontStyle: "italic" }}>
                    (Available only in the live app)
                  </SoftTypography>
              }
            </AccountSection>

            {/** Integrated Accounts section */}
            {(isAdmin) &&
              <AccountSection
                icon={LinkOutlinedIcon}
                title="Connected accounts"
                subtitle="Third-party accounts linked to your profile"
              >
                <IntegratedAccounts />
              </AccountSection>
            }

            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
              {/** Account Cancellation — applied to AccountSection's own root
                  div via child selectors: stretch it to the row's full
                  height, then let the subtitle (the element between the
                  title and the button) grow to absorb the extra space, so
                  the button lands at the same height as Log out's without
                  drifting the subtitle away from its title. */}
              <div className="w-full sm:flex-1 sm:min-w-0 [&>div]:!mb-0 [&>div]:h-full [&>div]:flex [&>div]:flex-col [&>div>p]:flex-1">
                <AccountSection
                  danger
                  icon={WarningAmberIcon}
                  title="Cancel account"
                  subtitle="This starts the cancellation process for your account."
                >
                  <AccountButton
                    onClick={handleCancelForm}
                    sx={{
                      fontSize: "13px !important",
                      lineHeight: "18px",
                      height: { mini: "40px", sm: "34px" },
                      width: { mini: "100%", sm: "auto" },
                      color: "#FFF !important",
                      backgroundColor: "#DC2626 !important",
                      border: "none",
                      "&:hover": { backgroundColor: "#B91C1C !important" },
                    }}
                  >
                    Cancel account
                  </AccountButton>
                </AccountSection>
              </div>

              {/** Account Email, Logout section — flex column so the button
                  pins to the bottom, matching the Cancel account card
                  regardless of which one has more text content. */}
              <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm w-full sm:flex-1 sm:min-w-0 flex flex-col justify-between">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Signed in as</p>
                  <p className="text-sm font-bold text-gray-900 mb-4 truncate">{userDetails?.email}</p>
                </div>

                <AccountButton
                  variant="outline"
                  disabled={isLoading}
                  onClick={handleLogout}
                  sx={{
                    fontSize: "13px !important",
                    lineHeight: "18px",
                    height: { mini: "40px", sm: "34px" },
                    width: { mini: "100%", sm: "auto" },
                    color: "#111827",
                    border: "1px solid #E5E7EB",
                    "& svg": {
                      marginRight: "6px"
                    }
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 16 }} />
                  Log out
                </AccountButton>
              </div>
            </div>

          </SoftBox>
        </form>
      </SoftBox>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          // glass modal scrim (light)
          sx: { backgroundColor: "rgba(15,23,42,0.16)" },
        }}
      >
        <Fade in={openModal}>
          <SoftBox sx={style}>
            <form ref={formRef}>
              {cancellationDone && (
                <SoftBox sx={{alignItems: 'center', textAlign: 'center'}}>
                  <svg width="71" height="71" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg" style={{margin: "0 auto"}}>
                    <circle cx="35.5" cy="35.5" r="35.2809" fill="url(#paint0_linear_149_11933)" fillOpacity="0.2" stroke="url(#paint1_linear_149_11933)" strokeWidth="0.438272"/>
                    <circle cx="35.5001" cy="35.5001" r="28.7068" fill="url(#paint2_linear_149_11933)" fillOpacity="0.5" stroke="url(#paint3_linear_149_11933)" strokeWidth="0.438272"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M47.7467 25.9868C48.1213 26.3618 48.3316 26.8701 48.3316 27.4001C48.3316 27.9301 48.1213 28.4384 47.7467 28.8134L31.4881 45.0721C31.3004 45.2596 31.0772 45.4079 30.8317 45.5083C30.5861 45.6088 30.323 45.6594 30.0577 45.6571C29.7924 45.6549 29.5302 45.5999 29.2863 45.4953C29.0425 45.3908 28.8219 45.2387 28.6374 45.0481L22.2294 38.4241C21.864 38.0422 21.6645 37.5313 21.6747 37.0028C21.6848 36.4744 21.9038 35.9714 22.2836 35.6039C22.6635 35.2364 23.1734 35.0342 23.7019 35.0415C24.2304 35.0487 24.7345 35.2649 25.1041 35.6428L30.0987 40.8054L44.9201 25.9868C45.2951 25.6122 45.8034 25.4019 46.3334 25.4019C46.8634 25.4019 47.3717 25.6122 47.7467 25.9868Z" fill="#299E47"/>
                    <defs>
                    <linearGradient id="paint0_linear_149_11933" x1="35.5" y1="3.28704" x2="35.7772" y2="104.192" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#299E47" stopOpacity="0"/>
                    <stop offset="0.300199" stopColor="#299E47" stopOpacity="0.06"/>
                    <stop offset="1" stopColor="#299E47"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear_149_11933" x1="35.5" y1="0" x2="35.5" y2="87.2161" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#98A2B3" stopOpacity="0.03"/>
                    <stop offset="1" stopColor="#41464D" stopOpacity="0.16"/>
                    </linearGradient>
                    <linearGradient id="paint2_linear_149_11933" x1="35.5001" y1="6.57422" x2="36.6752" y2="98.3835" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#299E47" stopOpacity="0.5"/>
                    <stop offset="0.356773" stopColor="#299E47" stopOpacity="0.2"/>
                    <stop offset="1" stopColor="#299E47" stopOpacity="0.7"/>
                    </linearGradient>
                    <linearGradient id="paint3_linear_149_11933" x1="35.5001" y1="6.57422" x2="35.5001" y2="64.4261" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#475467" stopOpacity="0.44"/>
                    <stop offset="1" stopColor="#8DA7CD" stopOpacity="0.06"/>
                    </linearGradient>
                    </defs>
                  </svg>
                  <SoftTypography sx={{textFillColor: "#0F172A", fontSize: '17px', fontWeight: '400', marginTop: '2rem', fontFamily: 'DM Sans'}}>
                    Your Cancellation is Complete
                  </SoftTypography>
                  <SoftTypography sx={{textFillColor: "#475467", fontSize: '14px', fontWeight: '400', marginTop: '2rem', fontFamily: 'DM Sans',
                    "& span": { color: "#7928CA!important" },}}>
                    Your account has been successfully canceled. If you have any questions or need further assistance, please contact info@gamemybiz.com
                  </SoftTypography>
                  <SoftButton
                      variant="outlined"
                      color="white"
                      sx={{
                        marginTop: "6rem",
                        "& svg": { fontSize: "1.25rem !important" },
                        minWidth: "48%",
                        padding: ".75rem",
                        borderRadius: "4px",
                        background: "#FFFFFF",
                        color: "#000000"
                      }}
                      onClick={handleCloseModal}
                    >
                      Close
                    </SoftButton>
                </SoftBox>
              )}
              {!cancellationDone && cancelForm &&  (
                <>
                  <SoftBox 
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center">
                    <SoftTypography sx={{textFillColor: "#D93D42", fontSize: '24px', fontWeight: '700', fontFamily: 'DM Sans'}}>
                      Cancel Account
                    </SoftTypography>
                    <SoftButton
                      variant="outlined"
                      color="white"
                      onClick={handleCloseModal}
                      sx={{
                        "& svg": { fontSize: "1.25rem !important" },
                        borderRadius: "50%",
                        minWidth: "max-content",
                        minHeight: "max-content",
                        padding: "0.25rem",
                        color: "#475467 !important",
                        borderColor: "rgba(15,23,42,0.10) !important",
                      }}
                    >
                      <CloseIcon />
                    </SoftButton>
                  </SoftBox>
                  {confirmation ? (
                    <SoftBox>
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '16px', fontWeight: '400', marginTop: '2rem', fontFamily: 'DM Sans'}}>
                        Before we proceed with the cancellation, we’d like to understand why you’re leaving. Your feedback helps us improve our services
                      </SoftTypography>
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '14px', fontWeight: '400', marginTop: '1rem', fontFamily: 'DM Sans'}}>
                        Please select a reason for your cancellation from the options below
                      </SoftTypography>
                      <SoftBox sx={{paddingLeft: '.75rem'}}>
                        <FormGroup>
                          <FormControlLabel
                            sx={{marginTop: '.5rem',
                              '& .MuiFormControlLabel-label': {
                                fontWeight: '400',
                                color: '#475467',
                                fontFamily: 'DM Sans',
                              },
                            }}
                            control={
                              <Checkbox
                                checked={selected === 'checkbox1'}
                                onChange={handleChange}
                                value="I no longer need the service."
                                name="checkbox1"
                              />
                            }
                            label="I no longer need the service."
                          />
                          <FormControlLabel
                            sx={{marginTop: '1rem',
                              '& .MuiFormControlLabel-label': {
                                fontWeight: '400',
                                color: '#475467',
                                fontFamily: 'DM Sans',
                              },}}
                            control={
                              <Checkbox
                                checked={selected === 'checkbox2'}
                                onChange={handleChange}
                                value="I found a better alternative."
                                name="checkbox2"
                              />
                            }
                            label="I found a better alternative."
                          />
                          <FormControlLabel
                            sx={{marginTop: '1rem',
                              '& .MuiFormControlLabel-label': {
                                fontWeight: '400',
                                color: '#475467',
                                fontFamily: 'DM Sans',
                              },}}
                            control={
                              <Checkbox
                                checked={selected === 'checkbox3'}
                                onChange={handleChange}
                                value="I had a technical issue."
                                name="checkbox3"
                              />
                            }
                            label="I had a technical issue."
                          />
                          <FormControlLabel
                            sx={{marginTop: '1rem',
                              '& .MuiFormControlLabel-label': {
                                fontWeight: '400',
                                color: '#475467',
                                fontFamily: 'DM Sans',
                              },}}
                            control={
                              <Checkbox
                                checked={selected === 'checkbox4'}
                                onChange={handleChange}
                                value="Other"
                                name="checkbox4"
                              />
                            }
                            label="Other (please specify)."
                          />
                          {showInput && (
                            <SoftInput
                              name="others"
                              placeholder="Others"
                              type="text"
                              autoComplete="off"
                              sx={{marginTop: "1rem", marginLeft: "1rem", background: "#F6F7F9!important", border: "1px solid #E4E7EC", borderRadius: "5px", maxWidth: "90%", color: "#1d2939!important"}}
                            />
                          )}

                        </FormGroup>
                      </SoftBox>
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '14px', fontWeight: '400', marginTop: '5rem', fontFamily: 'DM Sans'}}>
                        You are about to cancel your account.
                      </SoftTypography>
                      <SoftTypography sx={{textFillColor: "#98A2B3", fontSize: '11px', fontWeight: '400', fontFamily: 'DM Sans'}}>
                        Please note: Any player seats added after your last billing cycle will be billed on your next billing date.
                      </SoftTypography>
                      <SoftBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{marginTop: "1rem"}}>
                        <SoftButton
                          variant="outlined"
                          color="white"
                          sx={{
                            "& svg": { fontSize: "1.25rem !important" },
                            minWidth: "48%",
                            padding: ".75rem",
                            borderRadius: "4px",
                            color: "#475467 !important",
                            background: "rgba(15,23,42,0.03)",
                            borderColor: "rgba(15,23,42,0.10) !important",
                            "&:hover": { background: "rgba(15,23,42,0.06)" }
                          }}
                          onClick={handleBtnSupport}
                        >
                          Yes, Contact Support
                        </SoftButton>
                        <SoftButton
                          variant="outlined"
                          color="white"
                          sx={{
                            "& svg": { fontSize: "1.25rem !important" },
                            minWidth: "48%",
                            padding: ".75rem",
                            borderRadius: "4px",
                            background: "#FFFFFF",
                            color: "#000000"
                          }}
                          onClick={handleBtnConfirmation}
                        >
                          No, Continue Cancellation
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  ) : (
                    <SoftBox>
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '16px', fontWeight: '400', marginTop: '2rem', fontFamily: 'DM Sans'}}>
                        Are you sure you want to cancel your account?
                      </SoftTypography>
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '16px', fontWeight: '400', marginTop: '1rem', fontFamily: 'DM Sans'}}>
                        This action is irreversible, and you will lose access to all your data and services associated with your account
                      </SoftTypography>
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '12px', fontWeight: '400', marginTop: '3rem', fontFamily: 'DM Sans'}}>
                        Type "CANCEL" to confirm
                      </SoftTypography>
                      <SoftInput
                          name="confirm"
                          placeholder="Type 'CANCEL' to confirm"
                          type="text"
                          autoComplete="off"
                          value={confirmText}
                          onChange={handleConfirmTextChange}
                          sx={{marginTop: "1rem", background: "#F6F7F9!important", border: "1px solid #E4E7EC", borderRadius: "5px", maxWidth: "90%", color: "#1d2939!important"}}
                      />
                      <SoftTypography sx={{textFillColor: "#475467", fontSize: '14px', fontWeight: '400', marginTop: '2rem', fontFamily: 'DM Sans'}}>
                        You are about to cancel your account.
                      </SoftTypography>
                      <SoftTypography sx={{textFillColor: "#98A2B3", fontSize: '11px', fontWeight: '400', fontFamily: 'DM Sans'}}>
                        Please note: Any player seats added after your last billing cycle will be billed on your next billing date.
                      </SoftTypography>
                      <SoftBox 
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{marginTop: "2rem"}}>
                        <SoftButton
                          variant="outlined"
                          color="white"
                          sx={{
                            "& svg": { fontSize: "1.25rem !important" },
                            minWidth: "48%",
                            padding: ".75rem",
                            borderRadius: "4px",
                            color: "#475467 !important",
                            background: "rgba(15,23,42,0.03)",
                            borderColor: "rgba(15,23,42,0.10) !important",
                            "&:hover": { background: "rgba(15,23,42,0.06)" }
                          }}
                          onClick={handleCloseModal}
                        >
                          Cancel Request
                        </SoftButton>
                        <SoftButton
                          variant="outlined"
                          color="white"
                          onClick={confirmCancellation}
                          disabled={confirmText !== 'CANCEL'}
                          sx={{
                            "& svg": { fontSize: "1.25rem !important" },
                            minWidth: "48%",
                            padding: ".75rem",
                            borderRadius: "4px",
                            background: "#FFFFFF",
                            color: "#000000!important",
                            cursor: confirmText !== 'CANCEL' ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Confirm Cancellation
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  )}
                  
                </>
              )} 
              {!cancellationDone  && supportContent &&  (
                <>
                  <SoftBox 
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center">
                    <SoftTypography sx={{textFillColor: "#0F172A", fontSize: '24px', fontWeight: '700', fontFamily: 'DM Sans'}}>
                      Customer care
                    </SoftTypography>
                    <SoftButton
                      variant="outlined"
                      color="white"
                      onClick={handleCloseModal}
                      sx={{
                        "& svg": { fontSize: "1.25rem !important" },
                        borderRadius: "50%",
                        minWidth: "max-content",
                        minHeight: "max-content",
                        padding: "0.25rem",
                        color: "#475467 !important",
                        borderColor: "rgba(15,23,42,0.10) !important",
                      }}
                    >
                      <CloseIcon />
                    </SoftButton>
                  </SoftBox>
                  <SoftTypography sx={{textFillColor: "#0F172A", fontSize: '16px', fontWeight: '400', marginTop: '3rem', fontFamily: 'DM Sans'}}>
                    Let's Resolve Your Issues!
                  </SoftTypography>
                  <SoftTypography sx={{textFillColor: "#475467", fontSize: '14px', fontWeight: '400', marginTop: '1rem', fontFamily: 'DM Sans'}}>
                    Our customer care team is here to help. Would you like to contact us to<br/> resolve any issues before canceling your account?
                  </SoftTypography>
                  <SoftBox 
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{marginTop: "7rem"}}>
                    <SoftButton
                      variant="outlined"
                      color="white"
                      sx={{
                        "& svg": { fontSize: "1.25rem !important" },
                        minWidth: "48%",
                        padding: ".75rem",
                        borderRadius: "4px",
                        color: "#475467 !important",
                        background: "rgba(15,23,42,0.03)",
                        borderColor: "rgba(15,23,42,0.10) !important",
                        "&:hover": { background: "rgba(15,23,42,0.06)" }
                      }}
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </SoftButton>
                    <SoftButton
                      variant="outlined"
                      color="white"
                      sx={{
                        "& svg": { fontSize: "1.25rem !important" },
                        minWidth: "48%",
                        padding: ".75rem",
                        borderRadius: "4px",
                        background: "#FFFFFF",
                        color: "#000000"
                      }}
                    >
                      Contact Customer care
                    </SoftButton>
                  </SoftBox>
                </>
              )}
            </form>
          </SoftBox>
        </Fade>
      </Modal>  
    </>
  );
};

export default AccountSettingsV2;
